import { gql, useLazyQuery } from "@apollo/client";
import { getErrors } from "../../utils";
import { AsyncQueryStatus } from "../../models/AsyncActionStatus";

export type MetricTrend = "FLAT" | "UP" | "DOWN";

export interface MetricTrendModel {
  key: MetricTrend;
  name: string;
}

export type TargetStatus = "NONE" | "BELOW" | "ON" | "ABOVE";

export type MetricLevelKey =
  | "Organization"
  | "Project"
  | "Register"
  | "PermitType"
  | "Permit";

export type MetricDimensionKey =
  | "None"
  | "State"
  | "Assignee"
  | "Month"
  | "PermitType"
  | "Project"
  | "Register";

// ---------------------------------------------------------------------------
// Input types — mirror PermitMetricQueryInput / PermitMetricScopeInput
// ---------------------------------------------------------------------------

export interface PermitMetricScopeInput {
  organizationId: string;
  projectIds?: string[] | null;
  registerIds?: string[] | null;
  teamMemberIds?: string[] | null;
  permitTypeIds?: string[] | null;
  stateKeys?: string[] | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

export interface PermitMetricQueryInput {
  scope: PermitMetricScopeInput;
  /** One of: Organization, Project, Register, PermitType, Permit */
  level?: MetricLevelKey | null;
  /** Optional grouping axis e.g. "State", "Project" */
  dimension?: MetricDimensionKey | null;
}

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

export interface MetricValueModel {
  value: number;
  previousValue: number | null;
  delta: number | null;
  deltaPercentage: number | null;
  trend: MetricTrendModel;
  target: number | null;
  targetStatus: TargetStatus;
}

export interface PermitsCountByStatesMetric_groups {
  __typename: "PermitCountGroupModel";
  dimensionKey: string;
  dimensionValue: string;
  value: MetricValueModel;
}

export interface PermitsCountByStatesMetric_result {
  __typename: "MetricPermitsCount";
  groups: PermitsCountByStatesMetric_groups[];
}

export interface PermitsCountByStatesMetric {
  permitsCountByStatesMetric: PermitsCountByStatesMetric_result;
}

export interface PermitsCountByStatesMetricVariables {
  query: PermitMetricQueryInput;
}

export const GET_PERMITS_COUNT_BY_STATES_METRIC = gql`
  query PermitsCountByStatesMetric($query: PermitMetricQueryInput!) {
    permitsCountByStatesMetric(query: $query) {
      groups {
        dimensionKey
        dimensionValue
        value {
          value
          previousValue
          delta
          deltaPercentage
          trend {
            key
            name
          }
        }
      }
    }
  }
`;

export function useGetPermitsCountByStatesMetric() {
  const [getPermitsCountByStatesMetric, { loading, error, data }] = useLazyQuery<
    PermitsCountByStatesMetric,
    PermitsCountByStatesMetricVariables
  >(GET_PERMITS_COUNT_BY_STATES_METRIC, {
    errorPolicy: "all",
    fetchPolicy: "cache-and-network",
  });

  return {
    getPermitsCountByStatesMetric,
    data,
    status: {
      loading,
      errors: getErrors(error),
      successful: !error && data ? true : false,
    } as AsyncQueryStatus,
  };
}
